import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { useCrmClientPermissions } from '../../../hooks/useCrmClientPermissions';
import { toTitleCase } from '../../../utils/formatters/text';
import { salesService } from '../../../services/sales.service';
import { CustomerProfile } from '../../../types/sales.types';
import { ApiError } from '../../../api/core/ApiError';
import { parseCustomerApiErrorMessage } from '../../../utils/customerApiMessages';
import { splitPhoneValue, validatePhoneByCountry } from '../../../utils/phone';

export const CRM_ADD_CLIENT_DENIED_TOOLTIP =
  'No tiene permiso para crear clientes. Solicite el permiso Añadir en Gestión de clientes.';
export const CRM_EDIT_CLIENT_DENIED_TOOLTIP =
  'No tiene permiso para editar clientes. Solicite el permiso Editar en Gestión de clientes.';

export type QuickCustomerDialogMode = 'create' | 'edit';

export function useSalesStep3Customer() {
  const { state, orderSummary, previousStep, nextStep, setSelectedCustomer, setHasInvoice } =
    useSalesModal();
  const { canAdd: canAddClient, canEdit: canEditClient } = useCrmClientPermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<CustomerProfile[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const requestSequenceRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [quickDialogOpen, setQuickDialogOpen] = useState(false);
  const [quickDialogMode, setQuickDialogMode] = useState<QuickCustomerDialogMode>('create');
  const [quickName, setQuickName] = useState('');
  const [quickCi, setQuickCi] = useState('');
  const [quickPhone, setQuickPhone] = useState('');
  const [quickNameError, setQuickNameError] = useState<string | null>(null);
  const [quickCiError, setQuickCiError] = useState<string | null>(null);
  const [quickFormError, setQuickFormError] = useState<string | null>(null);
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [quickCreateAck, setQuickCreateAck] = useState(false);

  const resetQuickForm = useCallback(() => {
    setQuickName('');
    setQuickCi('');
    setQuickPhone('');
    setQuickNameError(null);
    setQuickCiError(null);
    setQuickFormError(null);
    setQuickCreateAck(false);
  }, []);

  const openQuickCreate = useCallback(() => {
    if (!canAddClient) return;
    resetQuickForm();
    setQuickDialogMode('create');
    setQuickDialogOpen(true);
  }, [canAddClient, resetQuickForm]);

  const openQuickEdit = useCallback(() => {
    if (!canEditClient || !state.selectedCustomer) return;
    const customer = state.selectedCustomer;
    setQuickName(customer.customerName ?? '');
    setQuickCi(customer.identityDocumentNumber?.trim() ?? '');
    setQuickPhone(customer.customerPhone?.trim() ?? '');
    setQuickNameError(null);
    setQuickCiError(null);
    setQuickFormError(null);
    setQuickDialogMode('edit');
    setQuickDialogOpen(true);
  }, [canEditClient, state.selectedCustomer]);

  const closeQuickDialog = useCallback(() => {
    if (isQuickSaving) return;
    setQuickDialogOpen(false);
    resetQuickForm();
    setQuickDialogMode('create');
  }, [isQuickSaving, resetQuickForm]);

  const handleSearch = useCallback(
    async (searchOverride?: string) => {
      const term = (searchOverride ?? searchTerm).trim();
      if (term.length < 2) return;

      const requestId = ++requestSequenceRef.current;
      setIsLoading(true);
      setHasSearched(true);
      try {
        const customers = await salesService.searchCustomers(term);
        if (requestId !== requestSequenceRef.current) return;
        setResults(customers);
      } catch (error) {
        if (requestId !== requestSequenceRef.current) return;
        console.error('Error searching customers on step 3:', error);
        setResults([]);
      } finally {
        if (requestId !== requestSequenceRef.current) return;
        setIsLoading(false);
      }
    },
    [searchTerm],
  );

  const handleQuickFormSubmit = useCallback(async () => {
    const isEdit = quickDialogMode === 'edit';
    if (isEdit) {
      if (!canEditClient || !state.selectedCustomer) return;
    } else if (!canAddClient || !quickCreateAck) {
      return;
    }

    const name = toTitleCase(quickName);
    const ci = quickCi.trim();
    const phone = quickPhone.trim();

    setQuickNameError(null);
    setQuickCiError(null);
    setQuickFormError(null);

    let hasFieldError = false;
    if (!name) {
      setQuickNameError('El nombre es obligatorio.');
      hasFieldError = true;
    }
    if (ci.length > 32) {
      setQuickCiError('Máximo 32 caracteres.');
      hasFieldError = true;
    }
    if (phone) {
      const { countryIso, nationalNumber } = splitPhoneValue(phone);
      const phoneErr = validatePhoneByCountry(countryIso, nationalNumber);
      if (phoneErr) {
        setQuickFormError(phoneErr);
        hasFieldError = true;
      }
    }
    if (hasFieldError) return;

    setIsQuickSaving(true);
    try {
      if (isEdit && state.selectedCustomer) {
        const updated = await salesService.updateCustomer(state.selectedCustomer.id, {
          customerName: name,
          ...(ci ? { identityDocumentNumber: ci } : {}),
          ...(phone ? { customerPhone: phone } : {}),
        });
        setSelectedCustomer(updated);
      } else {
        const created = await salesService.createCustomer({
          customerName: name,
          marketingConsent: false,
          ...(ci ? { identityDocumentNumber: ci } : {}),
          ...(phone ? { customerPhone: phone } : {}),
        });
        setSelectedCustomer(created);
        setResults([]);
        setHasSearched(false);
        setSearchTerm('');
        setShowDropdown(false);
      }
      setQuickDialogOpen(false);
      resetQuickForm();
      setQuickDialogMode('create');
    } catch (error) {
      const msg = parseCustomerApiErrorMessage(error);
      if (error instanceof ApiError && error.status === 403) {
        setQuickFormError(
          isEdit
            ? 'No tiene permiso para editar clientes.'
            : 'No tiene permiso para crear clientes.',
        );
        return;
      }
      if (
        msg.toLowerCase().includes('ya existe un cliente') ||
        msg.toLowerCase().includes('documento de identidad')
      ) {
        setQuickCiError(msg);
        return;
      }
      setQuickFormError(msg);
    } finally {
      setIsQuickSaving(false);
    }
  }, [
    canAddClient,
    canEditClient,
    quickCi,
    quickCreateAck,
    quickDialogMode,
    quickName,
    quickPhone,
    resetQuickForm,
    setSelectedCustomer,
    state.selectedCustomer,
  ]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      requestSequenceRef.current += 1;
      setIsLoading(false);
      setResults([]);
      setHasSearched(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      void handleSearch(term);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, handleSearch]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchTerm(value);

      if (value.trim().length >= 2) {
        setShowDropdown(true);
        if (
          state.selectedCustomer &&
          value.trim() !== (state.selectedCustomer.customerName?.trim() || '')
        ) {
          setSelectedCustomer(null);
        }
      } else {
        setShowDropdown(false);
      }
    },
    [setSelectedCustomer, state.selectedCustomer],
  );

  const handleSearchFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setAnchorEl(event.currentTarget);
    if (event.currentTarget.value.trim().length >= 2) {
      setShowDropdown(true);
    }
  }, []);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowDropdown(false), 150);
  }, []);

  const handleCustomerSelect = useCallback(
    (customer: CustomerProfile) => {
      setSelectedCustomer(customer);
      setShowDropdown(false);
      setSearchTerm('');
      setResults([]);
      setHasSearched(false);
    },
    [setSelectedCustomer],
  );

  const canShowDropdown = searchTerm.trim().length >= 2;

  const handleContinueWithoutCustomer = () => {
    setSelectedCustomer(null);
    nextStep();
  };

  const handleClearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setSearchTerm('');
  };

  const handleContinueStep4 = () => {
    if (!state.selectedCustomer) return;
    nextStep();
  };

  const handleResetCustomerSearch = () => {
    requestSequenceRef.current += 1;
    setSearchTerm('');
    setResults([]);
    setHasSearched(false);
    setIsLoading(false);
    setSelectedCustomer(null);
    setShowDropdown(false);
  };

  return {
    state,
    orderSummary,
    previousStep,
    setHasInvoice,
    canAddClient,
    canEditClient,
    searchTerm,
    isLoading,
    results,
    hasSearched,
    showDropdown,
    anchorEl,
    searchInputRef,
    quickDialogOpen,
    quickDialogMode,
    quickName,
    setQuickName,
    quickCi,
    setQuickCi,
    quickPhone,
    setQuickPhone,
    quickNameError,
    quickCiError,
    quickFormError,
    isQuickSaving,
    quickCreateAck,
    setQuickCreateAck,
    openQuickCreate,
    openQuickEdit,
    closeQuickDialog,
    handleQuickFormSubmit,
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleCustomerSelect,
    canShowDropdown,
    handleContinueWithoutCustomer,
    handleClearSelectedCustomer,
    handleContinueStep4,
    handleResetCustomerSearch,
  };
}
